import React, {PropsWithChildren} from 'react';
import {useTranslation} from 'react-i18next';
import {i18n} from '../configs/i18n/i18n';
import {loadStripe} from '@stripe/stripe-js/pure';
import {Elements} from '@stripe/react-stripe-js';
import {CHEKIN_ROOT_IFRAME_ID} from '../utils/embedded';
import {Stripe, StripeElementLocale} from '@stripe/stripe-js';
import {getEnvVariable} from '../configs';
import {usePaymentSettings} from '../hooks';
import {useFrame} from './frame';
import {getFeatureFlags} from 'utils/features';
import {useTheme} from 'styled-components';

type StripeProviderProps = PropsWithChildren;

const loadStripeToIframe = (
  stripeKey: string,
  {stripeAccount}: {stripeAccount?: string},
) => {
  return new Promise<Stripe | null>((resolve, reject) => {
    const rawFrame = document.getElementById(CHEKIN_ROOT_IFRAME_ID) as HTMLIFrameElement;
    const iframeWindow = rawFrame?.contentWindow;
    const frameDoc = iframeWindow?.document;
    const existingScript = frameDoc?.getElementById('stripe-script');
    if (existingScript) return resolve(null);

    const stripeJs = document.createElement('script');

    stripeJs.src = 'https://js.stripe.com/v3/';
    stripeJs.async = true;
    stripeJs.id = 'stripe-script';

    stripeJs.onload = () => {
      if (iframeWindow?.Stripe) {
        const stripe = iframeWindow.Stripe(stripeKey, {stripeAccount});
        resolve(stripe);
      } else {
        reject(i18n.t('stripe_object_is_not_found'));
      }
    };

    frameDoc?.head.appendChild(stripeJs);
  });
};

function StripeProvider({children}: StripeProviderProps) {
  const {i18n} = useTranslation();
  const {frame} = useFrame();
  const theme = useTheme();
  const {paymentSettings, isStripePaymentProvider, currency} = usePaymentSettings();
  const stripeAccount = paymentSettings?.external_account_id;

  const options = React.useMemo(() => {
    return getFeatureFlags('IT_4491_STRIPE_PAYMENT_FORM_V2')
      ? {
          locale: i18n.language as StripeElementLocale,
          mode: 'setup' as const,
          currency: currency?.toLowerCase() || 'eur',
          appearance: {
            variables: {
              colorPrimary: theme.colors.branding.primary,
              colorBackground: '#ffffff',
              colorText: theme.colors.branding.fontPrimary,
              fontFamily: 'system-ui, sans-serif',
              borderRadius: '8px',
            },
          },
          loader: 'never' as const,
        }
      : {
          locale: i18n.language as StripeElementLocale,
        };
  }, [i18n.language, theme, currency]);

  const stripePromise = React.useMemo(() => {
    const needLoadStripe = isStripePaymentProvider && stripeAccount;
    const stripeKey = getEnvVariable('VITE_APP_STRIPE_API_KEY');

    if (!needLoadStripe) {
      return null;
    }

    if (frame) {
      return loadStripeToIframe(stripeKey, {stripeAccount});
    }

    return loadStripe(stripeKey, {stripeAccount});
  }, [frame, isStripePaymentProvider, stripeAccount]);

  return (
    <Elements
      children={children}
      stripe={stripePromise}
      key={stripeAccount}
      options={options}
    />
  );
}

export {StripeProvider};
